import { CircleAlert, CircleCheck, CircleX, Info, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { WhatsAppTemplate } from '@/Domain/api/WhatsAppTemplates/WhatsAppTemplates.api';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { WhatsappIcon } from '@/Domain/components/Shared/BrandIcon/BrandIcon';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  useBulkSendWhatsAppTemplate,
  useValidatePhones,
  type BulkSendRecipientResult,
  type PhoneValidationResult,
} from '@/Domain/hooks/Conversation/useBulkWhatsAppTemplate.hook';
import { useWhatsAppTemplates } from '@/Domain/hooks/Conversation/useWhatsAppTemplates.hook';
import { useWhatsAppSetupStatus } from '@/Domain/hooks/Integration/useWhatsAppSetupStatus.hook';
import {
  applyParamValues,
  buildTemplateComponents,
  getBodyText,
  getTemplateText,
  parseParams,
} from '@/Domain/utils/whatsappTemplateParams';

const MAX_RECIPIENTS = 100;

type Step = 'input' | 'confirm' | 'results';

interface PhoneEntry {
  raw: string;
  isDuplicate: boolean;
}

function isValidPhoneNumber(phone: string): boolean {
  const digits = phone.replace(/[^\d]/g, '');
  return digits.length >= 7;
}

function buildPhoneEntries(numbers: string[]): PhoneEntry[] {
  const seen = new Set<string>();
  return numbers.map((raw) => {
    const isDuplicate = seen.has(raw);
    seen.add(raw);
    return { raw, isDuplicate };
  });
}

interface BulkWhatsAppTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BulkWhatsAppTemplateDialog = ({
  open,
  onOpenChange,
}: BulkWhatsAppTemplateDialogProps) => {
  const { t } = useTranslation('inbox_v2');
  const [step, setStep] = useState<Step>('input');
  const [phones, setPhones] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [validationResults, setValidationResults] = useState<PhoneValidationResult[]>([]);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const whatsAppStatus = useWhatsAppSetupStatus().data?.status;
  const isWhatsAppConnected = whatsAppStatus === 'connected';
  const {
    templates,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useWhatsAppTemplates(open && isWhatsAppConnected);
  const validatePhones = useValidatePhones();
  const bulkSend = useBulkSendWhatsAppTemplate();

  // The Inbox fails open while /status/ is loading, so the dialog can open before status resolves; close it once status is definitively not-connected (entry point already disabled, templates never load).
  useEffect(() => {
    if (open && whatsAppStatus != null && whatsAppStatus !== 'connected') {
      onOpenChange(false);
    }
  }, [open, whatsAppStatus, onOpenChange]);

  const phoneEntries = useMemo(() => buildPhoneEntries(phones), [phones]);

  const uniquePhones = useMemo(() => {
    const seen = new Set<string>();
    return phoneEntries.filter((p) => {
      if (seen.has(p.raw)) return false;
      seen.add(p.raw);
      return true;
    });
  }, [phoneEntries]);

  const validPhones = useMemo(
    () => uniquePhones.filter((p) => isValidPhoneNumber(p.raw) && !p.isDuplicate),
    [uniquePhones],
  );

  const templateParams = useMemo(() => {
    if (!selectedTemplate) return [];
    const bodyText = getBodyText(selectedTemplate);
    return bodyText ? parseParams(bodyText) : [];
  }, [selectedTemplate]);

  const blurCommittedRef = useRef(false);

  const commitPhones = useCallback((text: string) => {
    const newNumbers = text
      .split(/[,\t\n\r]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (newNumbers.length > 0) {
      setPhones((prev) => [...prev, ...newNumbers]);
    }
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      blurCommittedRef.current = false;
      // Check if the user typed a comma or newline — commit everything before it
      if (value.includes(',') || value.includes('\t')) {
        commitPhones(value);
        setCurrentInput('');
      } else {
        setCurrentInput(value);
      }
    },
    [commitPhones],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === 'Tab') {
        const trimmed = currentInput.trim();
        if (trimmed) {
          e.preventDefault();
          blurCommittedRef.current = true;
          commitPhones(trimmed);
          setCurrentInput('');
        }
      } else if (e.key === 'Backspace' && currentInput === '' && phones.length > 0) {
        setPhones((prev) => prev.slice(0, -1));
      }
    },
    [currentInput, phones.length, commitPhones],
  );

  const handleInputBlur = useCallback(() => {
    if (blurCommittedRef.current) return;
    const trimmed = currentInput.trim();
    if (trimmed) {
      blurCommittedRef.current = true;
      commitPhones(trimmed);
      setCurrentInput('');
    }
  }, [currentInput, commitPhones]);

  const handleInputPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData('text');
      if (pasted.includes(',') || pasted.includes('\n') || pasted.includes('\t')) {
        e.preventDefault();
        commitPhones(pasted);
        setCurrentInput('');
      }
    },
    [commitPhones],
  );

  const handleRemovePhone = useCallback((index: number) => {
    setPhones((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleTemplateSelect = useCallback((template: WhatsAppTemplate | null) => {
    setSelectedTemplate(template);
    setParamValues({});
  }, []);

  const handleReviewClick = useCallback(() => {
    const phoneNumbers = validPhones.map((p) => p.raw);
    validatePhones.mutate(
      { phone_numbers: phoneNumbers },
      {
        onSuccess: (data) => {
          setValidationResults(data.results);
          setStep('confirm');
        },
      },
    );
  }, [validPhones, validatePhones]);

  const handleSendClick = useCallback(() => {
    if (!selectedTemplate) return;
    const phoneNumbers = validPhones.map((p) => p.raw);

    const components = buildTemplateComponents(selectedTemplate, paramValues);
    const resolvedBodyText = applyParamValues(
      getTemplateText(selectedTemplate),
      templateParams,
      paramValues,
    );

    bulkSend.mutate(
      {
        phone_numbers: phoneNumbers,
        template_name: selectedTemplate.name,
        language_code: selectedTemplate.language,
        components: components.length > 0 ? components : null,
        body_text: resolvedBodyText,
      },
      {
        onSettled: () => {
          setStep('results');
        },
      },
    );
  }, [validPhones, selectedTemplate, bulkSend, paramValues, templateParams]);

  const handleClose = useCallback(() => {
    setStep('input');
    setPhones([]);
    setCurrentInput('');
    setSelectedTemplate(null);
    setValidationResults([]);
    setParamValues({});
    bulkSend.reset();
    validatePhones.reset();
    onOpenChange(false);
  }, [onOpenChange, bulkSend, validatePhones]);

  const existingConversationCount = useMemo(
    () => validationResults.filter((r) => r.has_existing_conversation).length,
    [validationResults],
  );

  const canReview =
    validPhones.length > 0 && validPhones.length <= MAX_RECIPIENTS && selectedTemplate !== null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WhatsappIcon className="size-4 text-green-500" />
            {step === 'confirm'
              ? t('bulk_send.confirm_title')
              : step === 'results'
                ? t('bulk_send.results_title')
                : t('bulk_send.dialog_title')}
          </DialogTitle>
          <DialogDescription />
        </DialogHeader>

        {step === 'input' && (
          <InputStep
            phoneEntries={phoneEntries}
            uniquePhones={uniquePhones}
            validPhoneCount={validPhones.length}
            currentInput={currentInput}
            templates={templates}
            isLoadingTemplates={isLoadingTemplates}
            isWhatsAppConnected={isWhatsAppConnected}
            templatesError={templatesError}
            selectedTemplate={selectedTemplate}
            templateParams={templateParams}
            paramValues={paramValues}
            onParamChange={setParamValues}
            onInputChange={handleInputChange}
            onInputKeyDown={handleInputKeyDown}
            onInputBlur={handleInputBlur}
            onInputPaste={handleInputPaste}
            onRemovePhone={handleRemovePhone}
            onTemplateSelect={handleTemplateSelect}
          />
        )}

        {step === 'confirm' && (
          <ConfirmStep
            templateName={selectedTemplate?.name ?? ''}
            phoneCount={validPhones.length}
            existingConversationCount={existingConversationCount}
            validationResults={validationResults}
          />
        )}

        {step === 'results' && <ResultsStep bulkSendData={bulkSend.data} />}

        <DialogFooter>
          {step === 'input' && (
            <Button
              variant="dark"
              size="sm"
              onClick={handleReviewClick}
              disabled={!canReview || validatePhones.isPending}
              data-testid="review-send-button"
            >
              {validatePhones.isPending ? (
                <Spinner className="h-4 w-4" />
              ) : (
                t('bulk_send.review_button')
              )}
            </Button>
          )}

          {step === 'confirm' && (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setStep('input')}>
                {t('bulk_send.back')}
              </Button>
              <Button
                variant="dark"
                size="sm"
                onClick={handleSendClick}
                loading={bulkSend.isPending}
                data-testid="confirm-send-button"
              >
                {t('bulk_send.confirm_send')}
              </Button>
            </div>
          )}

          {step === 'results' && (
            <Button
              variant="dark"
              size="sm"
              onClick={handleClose}
              data-testid="close-results-button"
            >
              {t('bulk_send.close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Sub-components ---

interface InputStepProps {
  phoneEntries: PhoneEntry[];
  uniquePhones: PhoneEntry[];
  validPhoneCount: number;
  currentInput: string;
  templates: WhatsAppTemplate[];
  isLoadingTemplates: boolean;
  isWhatsAppConnected: boolean;
  templatesError: Error | null;
  selectedTemplate: WhatsAppTemplate | null;
  templateParams: string[];
  paramValues: Record<string, string>;
  onParamChange: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onInputBlur: () => void;
  onInputPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onRemovePhone: (index: number) => void;
  onTemplateSelect: (template: WhatsAppTemplate | null) => void;
}

const InputStep = ({
  phoneEntries,
  uniquePhones,
  validPhoneCount,
  currentInput,
  templates,
  isLoadingTemplates,
  isWhatsAppConnected,
  templatesError,
  selectedTemplate,
  templateParams,
  paramValues,
  onParamChange,
  onInputChange,
  onInputKeyDown,
  onInputBlur,
  onInputPaste,
  onRemovePhone,
  onTemplateSelect,
}: InputStepProps) => {
  const { t } = useTranslation('inbox_v2');
  const inputRef = useRef<HTMLInputElement>(null);
  const validCount = uniquePhones.filter((p) => isValidPhoneNumber(p.raw) && !p.isDuplicate).length;

  const handleTemplateChange = useCallback(
    (value: string) => {
      const template = templates.find((tpl) => tpl.id === value) ?? null;
      onTemplateSelect(template);
    },
    [templates, onTemplateSelect],
  );

  const handleContainerClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const bodyText = selectedTemplate ? getBodyText(selectedTemplate) : undefined;

  const previewText = useMemo(
    () =>
      bodyText && templateParams.length > 0
        ? applyParamValues(bodyText, templateParams, paramValues)
        : (bodyText ?? ''),
    [bodyText, templateParams, paramValues],
  );

  const isSingleRecipient = validPhoneCount === 1;
  const hasParams = templateParams.length > 0;
  // A disabled templates query reports isLoading=false, so also treat the
  // not-yet-connected window as pending to avoid an enabled, empty Select.
  const isTemplateSelectPending = isLoadingTemplates || !isWhatsAppConnected;

  return (
    <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0 px-1 pb-1">
      {/* Phone numbers input */}
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          {t('bulk_send.phone_input_label')}
          <span
            className={`font-normal ${validCount > MAX_RECIPIENTS ? 'text-red-600' : 'text-foreground-subtle'}`}
          >
            ({validCount} / {MAX_RECIPIENTS})
          </span>
        </label>
        <div
          className="flex flex-wrap content-start gap-1.5 min-h-[96px] max-h-48 overflow-y-auto rounded-xl border border-border px-3 py-2 cursor-text focus-within:ring focus-within:ring-opacity-25 focus-within:border-foreground focus-within:ring-ring"
          onClick={handleContainerClick}
          data-testid="phone-input"
        >
          {phoneEntries.map((phone, index) => {
            const isValid = isValidPhoneNumber(phone.raw);
            const isDuplicate = phone.isDuplicate;
            return (
              <span
                key={`${phone.raw}-${index}`}
                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-sm ${
                  isDuplicate
                    ? 'bg-orange-50 text-orange-700 border border-orange-200'
                    : isValid
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-red-50 text-red-700 border border-red-200'
                }`}
                data-testid={`phone-chip-${index}`}
              >
                {phone.raw}
                <button
                  type="button"
                  className="ml-0.5 hover:opacity-70"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemovePhone(index);
                  }}
                  data-testid={`phone-chip-remove-${index}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            type="text"
            className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
            value={currentInput}
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            onBlur={onInputBlur}
            onPaste={onInputPaste}
            placeholder={phoneEntries.length === 0 ? t('bulk_send.phone_input_placeholder') : ''}
            data-testid="phone-text-input"
          />
        </div>
        <p className="text-xs text-foreground-subtle">{t('bulk_send.country_code_hint')}</p>
        {validCount > MAX_RECIPIENTS && (
          <p className="text-sm text-red-600">
            {t('bulk_send.phone_limit_warning', { max: MAX_RECIPIENTS })}
          </p>
        )}
      </div>

      {/* Template selector */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t('bulk_send.select_template_label')}
        </label>
        {templatesError ? (
          <div
            className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2"
            data-testid="template-error"
          >
            <CircleAlert className="size-4 text-red-500 shrink-0" />
            <span className="text-sm text-red-700">{t('bulk_send.template_load_error')}</span>
          </div>
        ) : (
          <Select
            value={selectedTemplate?.id ?? ''}
            onValueChange={handleTemplateChange}
            disabled={isTemplateSelectPending}
          >
            <SelectTrigger data-testid="template-select-trigger">
              <SelectValue
                placeholder={
                  isTemplateSelectPending
                    ? t('whatsapp_template.loading')
                    : t('bulk_send.select_template_placeholder')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {templates.map((tpl) => (
                <SelectItem key={tpl.id} value={tpl.id} data-testid={`template-option-${tpl.id}`}>
                  <span>{tpl.name}</span>
                  <span className="ml-2 text-xs text-foreground-subtle">{tpl.language}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <p className="text-sm text-foreground-subtle">
          <Trans
            t={t}
            i18nKey="bulk_send.template_hint"
            components={{
              wa: (
                <a
                  href="https://business.facebook.com/latest/whatsapp_manager/message_templates"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-foreground hover:!underline"
                />
              ),
            }}
          />
        </p>
      </div>

      {/* Template variable inputs (single recipient only) */}
      {hasParams && isSingleRecipient && (
        <div className="flex flex-col gap-2" data-testid="template-params">
          <label className="text-sm font-medium text-muted-foreground">
            {t('bulk_send.template_variables_label')}
          </label>
          {templateParams.map((param, index) => (
            <div key={param}>
              <label className="text-xs font-medium text-foreground-subtle mb-1 block">
                {/\{\{\d+\}\}/.test(param)
                  ? t('whatsapp_template.preview.param_label', { number: index + 1 })
                  : param.replace(/\{\{|\}\}/g, '')}
              </label>
              <Input
                size="sm"
                data-testid={`template-param-input-${index}`}
                placeholder={t('whatsapp_template.preview.param_placeholder')}
                value={paramValues[param] || ''}
                onChange={(e) => onParamChange((prev) => ({ ...prev, [param]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      )}

      {/* Multi-recipient variable warning */}
      {hasParams && validPhoneCount > 1 && (
        <div
          className="flex items-start gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2"
          data-testid="template-variables-warning"
        >
          <CircleAlert className="size-4 text-orange-500 mt-0.5 shrink-0" />
          <span className="text-sm text-orange-700">
            {t('bulk_send.template_variables_warning')}
          </span>
        </div>
      )}

      {/* Template body preview */}
      {bodyText && (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-muted-foreground">
            {t('whatsapp_template.preview.body_label')}
          </span>
          <div
            className="text-sm text-muted-foreground bg-muted rounded-lg p-2.5 whitespace-pre-wrap"
            data-testid="template-body-preview"
          >
            {previewText}
          </div>
        </div>
      )}
    </div>
  );
};

interface ConfirmStepProps {
  templateName: string;
  phoneCount: number;
  existingConversationCount: number;
  validationResults: PhoneValidationResult[];
}

const ConfirmStep = ({
  templateName,
  phoneCount,
  existingConversationCount,
  validationResults,
}: ConfirmStepProps) => {
  const { t } = useTranslation('inbox_v2');

  return (
    <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0">
      <p className="text-sm text-muted-foreground">
        {t('bulk_send.confirm_message', { template: templateName, count: phoneCount })}
      </p>

      {existingConversationCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
          <CircleAlert className="size-4 text-orange-500 shrink-0" />
          <span className="text-sm text-orange-700">
            {t('bulk_send.confirm_existing_warning', { count: existingConversationCount })}
          </span>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
        <Info className="size-4 text-blue-500 mt-0.5 shrink-0" />
        <span className="text-sm text-blue-700">{t('bulk_send.confirm_ai_info')}</span>
      </div>

      {/* Validation results list */}
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          {t('bulk_send.phone_input_label')}
          <span className="font-normal text-foreground-subtle">({validationResults.length})</span>
        </label>
        <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
          {validationResults.map((result, index) => (
            <div
              key={index}
              className="flex items-center justify-between px-3 py-1.5 rounded-md bg-muted text-sm"
            >
              <span className="text-muted-foreground font-mono text-xs">
                {result.normalized || result.phone_number}
              </span>
              <div className="flex items-center gap-1.5">
                {!result.valid && (
                  <Badge variant="error" size="xs">
                    {t('bulk_send.phone_invalid')}
                  </Badge>
                )}
                {result.has_existing_conversation && (
                  <Badge variant="warning" size="xs">
                    {t('bulk_send.badge_existing')}
                  </Badge>
                )}
                {result.valid && !result.has_existing_conversation && (
                  <Badge variant="success" size="xs">
                    {t('bulk_send.badge_new')}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ResultsStepProps {
  bulkSendData:
    | {
        total: number;
        succeeded: number;
        failed: number;
        results: BulkSendRecipientResult[];
      }
    | undefined;
}

const ResultsStep = ({ bulkSendData }: ResultsStepProps) => {
  const { t } = useTranslation('inbox_v2');

  if (!bulkSendData) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-sm text-foreground-subtle">{t('bulk_send.sending')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <CircleCheck className="size-4 text-green-500" />
          <span className="text-sm font-medium">
            {t('bulk_send.results_success', {
              succeeded: bulkSendData.succeeded,
              total: bulkSendData.total,
            })}
          </span>
        </div>
        {bulkSendData.failed > 0 && (
          <div className="flex items-center gap-2">
            <CircleX className="size-4 text-red-500" />
            <span className="text-sm font-medium text-red-600">
              {t('bulk_send.results_failed', { failed: bulkSendData.failed })}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
        {bulkSendData.results.map((result, index) => (
          <div
            key={index}
            className="flex items-center justify-between px-3 py-1.5 rounded-md bg-muted text-sm"
          >
            <span className="text-muted-foreground font-mono text-xs">{result.phone_number}</span>
            {result.success ? (
              <Badge variant="success" size="xs">
                {t('bulk_send.badge_sent')}
              </Badge>
            ) : (
              <Badge variant="error" size="xs">
                {result.error || t('bulk_send.badge_failed')}
              </Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
